import React, { useState, useEffect } from 'react';
import { injectIntl, FormattedMessage } from 'react-intl';
import { available_locales, formatBigNumber, getStageName, i18nIntl, isEmpty, isNotEmpty, validateEmail } from '../../lib/ep.js';
import { v4 as uuidv4 } from 'uuid';
import { Provider } from 'react-redux';

import ActionLink from '../../components/builder/ActionLink';
import Checkbox from '../../components/builder/Checkbox';
import ImageUploader, { AutoPostImageUploader } from '../../components/builder/ImageUploader';
import Input from '../../components/builder/Input';
import Message, { AlertMessage, InformationMessage } from '../../components/builder/Message';
import Model from '../../components/builder/Model';
import RichEditor from '../../components/builder/RichEditor';
import Select from '../../components/builder/Select';
import DynamicFields from '../../components/builder/DynamicFields';
import DynamicForm, { DynamicFormBuilder } from '../../components/builder/DynamicForm';

import AddCodeSet from '../../components/codesets/AddCodeSet';
import BuilderSubSectionTitle from '../../components/backend/BuilderSubSectionTitle.js';
import { qr_code_generate } from '../../components/backend/StageEdit.js';

import QRViewer from '../../components/ui_items/QRViewer.js';
import FloatingLayer from '../../components/builder/FloatingLayer.js';
import ButtonCollection from '../../components/builder/ButtonCollection.js';
import ActionButton, { SecondaryButton } from '../../components/builder/ActionButton.js';
import SaveButton from '../../components/builder/SaveButton.js';
import SectionTitle from '../../components/builder/SectionTitle.js';
import SectionSubtitle from '../../components/builder/SectionSubtitle.js';
import MultiPicker from '../../components/builder/MultiPicker.js';
import { stage_categories, stage_promotion_types as stage_promotion_types } from '../../data/bootstrap_stages';
import ItemAsRibbons from '../../components/builder/ItemAsRibbons.js';
import Tooltip from '../../components/builder/Tooltip.js';
import NavigationTabs from '../../components/builder/NavigationTabs.js';

import transferPlaceholder from '../../hoc/transferPlaceholder';
import DisabledArea from '../../components/builder/DisabledArea.js';
import PreviewEmail from '../../components/emails/PreviewEmail.js';
import TriviaQuestion from '../../components/front/TriviaQuestion.js';
import OcrTicketSidebar from '../../components/backend/OcrTicketSidebar.js';
import OCRPacksStatus from '../../components/backend/TicketOcr/OCRPacksStatus.js';
import ToggleContent from '../../components/Form/ToggleContent.js';
import { default_waiting_sentences } from './OcrTicketProcess.js';
import { useAccount } from '../../components/context/accountContext.js';
import { useOwner } from '../../components/context/context.js';
const InputPlaceHolder = injectIntl(transferPlaceholder(Input));
const RichPlaceHolder = injectIntl(transferPlaceholder(RichEditor));

export const RejectionField = ({ model, change, ...props }) => {
	return (
		<Model model={model} change={change}>
			<Input
				name="title"
				_class={{ size: 80 }}
			>Reason</Input>
		</Model>
	)
};

const TicketPremoderationMailWrapper = ({ type, state, change, openPreview, ...props }) => {
	const label = (type == 'ok') ? "Send an automatic email to participant upon receipt acceptance" : "Send an automatic email to participant upon receipt rejection";

	return (
		<>
			{(type == 'ko') && (
				<DisabledArea disabled={!props.promotion.need_email}>
					<Model model={state} change={change} >
						<Checkbox
							key={`reqs_premoderation_${type}_enabled`}
							name={`reqs_premoderation_${type}_enabled`}
						>{label}</Checkbox>
					</Model>
				</DisabledArea>
			)}
			{props.children}
		</>
	)
};

const WaitingSentenceField = ({ model, change, ...props }) => {
	return (
		<Model model={model} change={change}>
			<Input
				name="text"
				_class={{ size:70 }}
			>Text</Input>
		</Model>
	)
};

const WaitingSentencesEditor = ({ stage, change, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const max_sentences = 10;

	const section_title = `${i18n("Waiting sentences")} (${((stage.reqs_waiting_sentences != undefined) ? stage.reqs_waiting_sentences.length : 0)}/${max_sentences})`;

	const dynamic_fields = [{
		type:'waiting_sentence',
		label:false,
		title:(f) => (f.text) ? `${f.text}` : `[ ${i18n("Empty") }]`,
		translate:[
			{ key:'text', type:'input' },
		],
	}];

	const avail_locales = [];
	if(window.langs != undefined) {
		window.langs.map((loc) => {
			avail_locales.push(loc.I18nPromotion.lang);
		})
	}
	
	const has_custom_waiting = stage?.reqs_waiting_sentences != undefined && stage?.reqs_waiting_sentences?.length > 0;
	const default_waiting = "<ul class='bulleted_list gui_not_in_top_small'><li>"+(default_waiting_sentences.map(ws=>ws.text).join("</li><li>")+"</li></ul>");
	return (
		<ToggleContent editing={has_custom_waiting}>
			
			<ToggleContent.ToggleButton>
				<Checkbox 
					name="enable_waiting"
					label_props={{
						tooltip:<Tooltip autotranslate={false} position='top'>{`${i18n("Processing the uploaded purchase receipt image may take between 10 and 60 seconds. By default, the system displays predefined sentences to inform the user that the receipt is being processed. You can modify this behavior by creating custom waiting sentences, which will be used instead of the system's default sentences")}:${default_waiting}`}</Tooltip>
					}}
				>Create custom waiting sentences</Checkbox>
			</ToggleContent.ToggleButton>
			
			<ToggleContent.Edit>
				<DynamicFields
					name="reqs_waiting_sentences"
					section_title={section_title}
					max_items={max_sentences}
					onChange={(v) => change('reqs_waiting_sentences', v)}
					section_classes='gui_not_in_top_huge'
					section_icon='knk-loading'
					field_types={dynamic_fields}
					add_field_label="Add"
					has_translation={true}
					locales={avail_locales}
					fields={stage?.reqs_waiting_sentences ?? []}
				>
					<WaitingSentenceField
						intl={props.intl}
					/>
				</DynamicFields>
			</ToggleContent.Edit>
		</ToggleContent>
	)
};

const TicketPremoderationMail = ({ type, tags, state, change, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);
	const [display_preview, setDisplayPreview] = useState(false);

	if(type == 'ko' && (!state[`reqs_premoderation_${type}_enabled`] || !props.promotion.need_email)) {
		return (
			<TicketPremoderationMailWrapper
				{...props}
				type={type}
				state={state}
				change={change}
			/>
		)
	}
	const is_advanced_ocr = ((state?.reqs_type ?? null) == '8') ? true : false;
	const has_tabs = props?.has_tabs ?? true;
	const direct_stage = (state.type == stage_categories.ID_FORM_CATEGORY.id) ? true : false;

	let default_placeholder = `${i18n("Dear")} ${(!props.promotion.hide_name) ? '{{user_first_name}}' : (props.promotion.need_nickname) ? '{{user_nickname}}' : ''}\n\n`;
	let subject_default_placeholder;
	const default_review_tickets = parseInt(state?.reqs_premoderation_inreview_tickets ?? 2);
	let _tags;
	switch (type) {
		case 'ok':
			subject_default_placeholder = "Your receipt has been approved!";
			default_placeholder += `${i18n("We are pleased to inform you that your receipt has been successfully reviewed and approved for our promotion.")}\n\n${i18n("Please click on the button below to continue with the participation")}:\n<a href="{{participation_url}}" class="button">${i18n("Participate", 'uppercase')}</a>`;
			_tags = tags.filter((t) => t.tag != 'reason');
			break;

		case 'ko':
		default:
			subject_default_placeholder = "Your receipt has been rejected";
			default_placeholder += `${i18n("We regret to inform you that your receipt has been rejected for our promotion.")}\n\n${i18n("Unfortunately, upon review, we found that the submitted receipt did not meet the requirements outlined in our promotion guidelines.")}\n\n${i18n("If you have any questions or concerns regarding this decision, please don't hesitate to contact us. We're here to assist you.")}\n\n${i18n("Thank you for your understanding.")}\n\n${i18n("Best regards,")}`;
			_tags = tags.filter((t) => t.tag != 'participation_url' && t.tag != 'participations');
			break;
	}

	return (
		<TicketPremoderationMailWrapper
			{...props}
			type={type}
			state={state}
			change={change}
		>
			<Model model={state} change={change}>
				<InputPlaceHolder
					key={`reqs_premoderation_subject_${type}`}
					name={`reqs_premoderation_subject_${type}`}
					_class={{ size: 80, first: (!has_tabs) ? true : false }}
					placeholder={subject_default_placeholder}
					value={state?.[`reqs_premoderation_subject_${type}`] ?? ''}
				>Subject</InputPlaceHolder>

				<RichPlaceHolder
					has_emojis={true}
					wrapStart="{{"
					wrapEnd="}}"
					key={`reqs_premoderation_email_${type}`}
					name={`reqs_premoderation_email_${type}`}
					_class={{ other: 'x_textarea' }}
					smarttags={_tags}
					placeholder={default_placeholder}
					buttons={true}
					label_props={{
						_extra_component: <ActionLink
							onClick={() => setDisplayPreview(true)}
						>preview</ActionLink>,
					}}
				>Body</RichPlaceHolder>

				<Select
					name="reqs_premoderation_inreview_tickets"
					_class={{ size: 20, other: 'gui_not_in_top_small' }}
					values={Array.from({ length: 10 }, (v, k) => ({ k:k + 1, v:k + 1 }))}
					value={default_review_tickets}
					label_props={{
						tooltip:<Tooltip>This security measure helps control system abuse and spam. It sets the maximum number of receipts a user can have pending review. By default, it's 1, with a maximum of 10.</Tooltip>,
					}}
				>Maximum number of pending receipts per user</Select>
				{(!is_advanced_ocr) && (
					<Checkbox
						name="reqs_premoderation_multiparticipation"
						label_props={{
							tooltip: <Tooltip>Activate to let pre-moderators assign multiple participations per receipt. They can specify the quantity via a selector. For instance, awarding 5 extra entries to a raffle or 5 more spins on roulette for receipts over $50.</Tooltip>,
						}}
					>Multi-participation assignment on receipt acceptance</Checkbox>
				)}

				{(type == 'ok' && direct_stage && isNotEmpty(state,'reqs_premoderation_multiparticipation')) && (
					<Checkbox
						name="reqs_premoderation_manual_participation"
						label_props={{
							tooltip:<Tooltip>When the receipt is accepted, the user will receive an automatic email with a link to complete their participation. This is the recommended option if the user is participating in an instant prize dynamic.</Tooltip>,
						}}
					>Complete participation from the email</Checkbox>
				)}
			</Model>

			{(display_preview) && (
				<FloatingLayer
					_class={{ size: 'normal' }}
					backButton={() => setDisplayPreview(false)}
				>
					<div class="without_actions">
						<PreviewEmail
							promotion={props.promotion}
							subject={isNotEmpty(state?.[`reqs_premoderation_subject_${type}`], ['']) ? state?.[`reqs_premoderation_subject_${type}`] : subject_default_placeholder}
						>{isNotEmpty(state?.[`reqs_premoderation_email_${type}`], ['']) ? state?.[`reqs_premoderation_email_${type}`] : default_placeholder}</PreviewEmail>
					</div>
				</FloatingLayer>
			)}
		</TicketPremoderationMailWrapper>
	)
};

export const TicketPremoderationLayer = ({ state, change, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const direct_stage = (state.type == stage_categories.ID_FORM_CATEGORY.id) ? true : false;
	const punch_card_stage = (state.type == stage_categories.ID_STAMPSCARD_CATEGORY.id) ? true : false;

	// The OK can be sent through the emails platform:
	const is_automatic_participation = ((direct_stage && !state.reqs_premoderation_manual_participation) || punch_card_stage) ? true : false;

	const [actual_tab, setActualTab] = useState('ok');//(!is_automatic_participation) ? 'ok' : 'ko'
	const [email_notification, setEmailNotification] = useState(null);

	const tags = [];
	let initial_state = null;
	useEffect(() => {
		if(!initial_state) initial_state = state;
	}, []);

	if(!props.promotion.hide_name) {
		tags.push({
			label: i18n("First name"),
			tag: 'user_first_name'
		});
		tags.push({
			label: i18n("Last name"),
			tag: 'user_last_name'
		});
	}
	if(props.promotion.need_nickname) {
		tags.push({
			label: i18n("Nickname"),
			tag: 'user_nickname'
		});
	}

	tags.push({
		label: i18n("Email"),
		tag: 'user_email'
	});
	tags.push({
		label: i18n("Promotion Title"),
		tag: 'promo_title'
	});
	tags.push({
		label: i18n("Reject reason"),
		tag: 'reason',
	});

	if(!is_automatic_participation) {
		tags.push({
			label: i18n("Participation URL"),
			tag: 'participation_url'
		})
	} else {
		tags.push({
			label: i18n("Participations assigned"),
			tag: 'participations'
		})
	}

	if(punch_card_stage) {
		tags.push({
			label: i18n("Stamp card link"),
			tag: 'stamp_card_link'
		})
	}

	const sections = [{
		label: i18n("Mail on acceptance"),
		icon: 'ep-thumbs-up-empty',
		onClick: () => setActualTab('ok'),
		selected: (actual_tab == 'ok') ? true : false,
	}, {
		label: i18n("Mail on rejection"),
		icon: 'ep-thumbs-down-empty',
		onClick: () => setActualTab('ko'),
		selected: (actual_tab == 'ko') ? true : false,
	}];

	const has_tabs = (sections.length > 0) ? true : false;

	const dynamic_fields = [{
		type: 'rejection',
		label: "Add reason",
		translate: [
			{ key: 'title', type: 'input' }
		],
		title: (f) => (f.title),
	}];

	const changeReason = (v) => {
		change('reqs_premoderation_reasons', v);
	};

	const addMailOnTicket = (newEmail) => {
		if(!validateEmail(newEmail)) {
			setEmailNotification('');
			return;
		}

		let emailsArr = (state?.reqs_premoderation_notifications ?? '').split(',');
		if(!emailsArr.includes(newEmail)) {
			// Si el email no existe, añadirlo al array
			emailsArr.push(newEmail);
		}
		change('reqs_premoderation_notifications', emailsArr.filter(email => email.trim() !== '').join(','));
		setEmailNotification('');
	};

	const removeMailOnTicket = (index) => {
		let emailsArr = (state?.reqs_premoderation_notifications ?? '').split(',');
		if(index >= 0 && index < emailsArr.length) {
			// Eliminar el elemento en el índice dado
			emailsArr.splice(index, 1);
		}
		change('reqs_premoderation_notifications', emailsArr.filter(email => email.trim() !== '').join(','));
	};

	const notification_ticket_arr = (state?.reqs_premoderation_notifications ?? '').split(',').filter(email => email.trim() !== '');

	const default_placeholder_msg = `<h2>${i18n("Thank you, we'll manually review the information provided as soon as possible.")}</h2><p>${i18n("We will let you know shortly if the receipt is valid.")}</p>`;

	const avail_locales = [];
	if(window.langs != undefined) {
		window.langs.map((loc) => {
			avail_locales.push(loc.I18nPromotion.lang);
		})
	}

	return (
		<FloatingLayer _class={{ size: 'big' }} backButton={props.backButton} enable_close={false}>
			<div>
				<BuilderSubSectionTitle
					_class="not_in_bottom"
					title="Receipt premoderation settings"
					title_actions={
						<ActionLink
							_href={`${window.zendesk_base_url}25023081612692`}
							_target='_blank'
						>Help tutorial</ActionLink>
					}
				/>
				<div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2em' }}>
					<div>
						<Model model={state} change={change}>
							<RichPlaceHolder
								name="reqs_premoderation_message"
								placeholder={default_placeholder_msg}
							>Message to user after uploading the receipt</RichPlaceHolder>

							<RichEditor
								name="reqs_premoderation_instructions"
								has_emojis={true}
							>Moderator instructions</RichEditor>

						</Model>

						<article class="gui_not_in_top_big">
							<DynamicFields
								section_title={i18n("Rejection reason")}
								onChange={changeReason}
								field_types={dynamic_fields}
								add_button_props={{
									icon: 'knk-add-item addButton',
								}}
								sortable={false}
								locales={avail_locales}
								has_translation={(avail_locales.length > 0) ? true : false}
								fields={state?.reqs_premoderation_reasons ?? []}
							>
								<RejectionField
									intl={props.intl}
								/>
							</DynamicFields>

							<Model model={state} change={change}>
								<Checkbox
									name="reqs_premoderation_reason_other"
									autotranslate={false}
								>{props.intl.formatMessage({ id: "Enable {type} field" }, { type: `"${i18n("Other")}"` })}</Checkbox>
							</Model>
						</article>
					</div>

					<div>

						<Model model={state} change={change}>

							{(props?.isCorporate ?? false) && (
								<Checkbox
									name="reqs_premoderation_display_data_user"
								>Display participant information to agents with premoderator role</Checkbox>
							)}

						</Model>

						{(!props.promotion.need_email) && (
							<Message
								_class={{ other: 'not_in_top_small in_bottom' }}
								icon="knk-information"
							>
								<p>{i18n("To be able to send notification emails to participants, it's necessary to request their email during user registration.")} <ActionLink _href={`#register`}>Go to registration</ActionLink>.</p>
							</Message>
						)}


						<ItemAsRibbons
							items={notification_ticket_arr}
							name='email_notification'
							onSubmit={addMailOnTicket}
							model={state}
							value={state.email_notification}
							change={(k, v) => setEmailNotification(v)}
							onDelete={removeMailOnTicket}
							placeholder="mail@domain.com"
							input_type='email'
							_class={{ other: 'not_in_top' }}
							label_props={{
								tooltip: <Tooltip>Enter one or more email addresses to receive a notification upon receiving a new receipt.</Tooltip>,
							}}
						>Notification upon receiving a new receipt</ItemAsRibbons>

						{(has_tabs) && (
							<NavigationTabs
								sections={sections}
								display_type='tab'
								_class={{ other: 'gui_not_in_top_big' }}
							/>
						)}

						<TicketPremoderationMail
							type={actual_tab}
							state={state}
							change={change}
							promotion={props.promotion}
							has_tabs={has_tabs}
							tags={tags}
							intl={props.intl}
						/>
					</div>
				</div>

				<ButtonCollection in_floating={true}>
					<SaveButton
						onClick={(e, cb) => {
							cb();
							setTimeout(() => {
								props.backButton()
								props.saveStage(state);
							}, 500);
						}}
					>Save and Exit</SaveButton>
				</ButtonCollection>
			</div>
		</FloatingLayer>
	)
};

export const SectionPremoderateTool = ({ state, change, saveStage, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const [ display_premoderation_layer, setDisplayPremoderationLayer ] = useState(false);
	const owner = useOwner();
	const checkbox_label = props?.label ?? "Enable receipt premoderation";
	const checkbox_tooltip = props?.tooltip ?? "When the user uploads the receipt, it will be pending review. A moderator or promotion administrator will have a console to review pending receipts and approve or reject them. The user will receive an email when their receipt has been approved or rejected. If approved, they can finalize their participation; if rejected, they will need to submit a new receipt. Click on configuration to customize the different messages and emails of the moderation process.";

	const is_advanced_ocr = ((state?.reqs_type ?? null) == '8') ? true : false;

	const activatePremoderation = (k,v) => {
		if(v && isEmpty(state,'reqs_premoderation_notifications',['']) && (owner?.agent?.Agent?.email ?? null)){
			change('reqs_premoderation_notifications', [owner.agent.Agent.email].filter(email => email.trim() !== '').join(','));
		}
		if(v)change('reqs_premoderation_manual_participation', true);
		change('reqs_premoderation',v);
	}

	return (
		<>
			{(!is_advanced_ocr) && (
				<Checkbox
					model={state}
					change={activatePremoderation}
					name="reqs_premoderation"
					after_component={(state.reqs_premoderation) ? <ActionLink
						onClick={() => setDisplayPremoderationLayer(true)}
					>Edit settings</ActionLink> : null}
					label_props={{
						tooltip:<Tooltip position='top'>{checkbox_tooltip}</Tooltip>,
					}}
				>{checkbox_label}</Checkbox>
			)}

			{(is_advanced_ocr) && (
				<ActionLink
					onClick={() => setDisplayPremoderationLayer(true)}
					_class={{ other:'not_in_top' }}
				>Open Manual Review settings</ActionLink>
			)}
			
			{(is_advanced_ocr && isEmpty(state,'reqs_premoderation_notifications',[''])) && (
				<span class="label_additional_msg gui_after_message whole_contents">{i18n("There is no email configured to receive notifications for new tickets requiring manual review.")}</span>
			)}


			{(display_premoderation_layer) && (
				<TicketPremoderationLayer
					state={state}
					change={change}
					promotion={props.promotion}
					backButton={() => setDisplayPremoderationLayer(false)}
					isCorporate={props.isCorporate}
					saveStage={saveStage}
					intl={props.intl}
				/>
			)}
		</>
	)
};

export const OCRTutorialMessage = (props) => {
	const in_top = props?.in_top ?? true;

	return (
		<Message
			_class={(in_top) ? { other: 'in_top' } : null}
		>
			<p>
				<FormattedMessage
					id="If all your receipts have the same format and you want to prevent users from uploading different photos of the same receipt, {link}."
					values={{
						link: <ActionLink
							_href={`${window.zendesk_base_url}20501518454292`}
							_target='_blank'
							_class={{ other: 'gui_inline' }}
						>use the configuration from the following tutorial</ActionLink>,
					}}
				/>
			</p>
		</Message>
	)
};

const ReqStage = ({ model, change, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);
	
	const score_options = [
		{ k:'', v:i18n("Have participated") },
		{ k:'min_score', v:i18n("Must have a minimum score") },
		{ k:'completed', v:i18n("Have completed the game") },
	];

	let stage_selector = [{ k:'', v:`-- ${i18n("Select a stage")} --` }];
	props.stages.map((_stage) => {
		const stage = _stage.Stage;

		if(stage.id != props.stage_id) {
			stage_selector.push({ k:stage.id, v:getStageName(stage, i18n) });
		}
	});

	return (
		<Model model={model} change={change}>
			<Select name="stage_id" _class={{ size: 70, other: 'gui_not_in_top_small' }} values={stage_selector}>Stage</Select>

			<Select name="score_options" _class={{ size: 70 }} values={score_options}>Condition</Select>

			{(model && model.score_options && model.score_options == 'min_score') && (
				<Input name="min_score" _class={{ size: 50 }}>Minimum score</Input>
			)}

			{model && (
				<Input name="condition_message">Condition message</Input>
			)}
		</Model>
	)
}

const StageReqsSidebar = ({ state, change, saveStage, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);
	const { MyAccount } = useAccount();

	const account_id = parseInt(MyAccount?.account?.account?.id ?? 0);

	const promotion = props?.promotion ?? {};
	const promo_type = parseInt(promotion?.promo_type ?? '2');

	const is_wl = (promo_type == 2) ? true : false;
	const is_premium = (promo_type == 1) ? true : false;

	const [ addCodes, toggleAddCodes ] = useState(false);
	const [ display_qr_regenerate_layer, setDisplayQRRegenerateLayer ] = useState(false);
	const [ display_dynamic_qr_info_layer, setDisplayDynamicQRInfoLayer ] = useState(false);

	const hasReqs = (state.reqs_type != undefined && state.reqs_type != '') ? true : false;
	const default_path = (props.default_path != undefined) ? props.default_path : '/';

	let codeset = {};
	const validation_sites = props.validationSitesStore.getState()?.validation_sites?.validation_sites;

	const codeSetAdded = (codeset) => {
		change('reqs_code_set', codeset.id);
	};

	useEffect(() => {
		if(state.reqs_code_type == 1) {
			//Usa un codigo unico
			change('reqs_code_one_use', false);
		}
	}, [ state.reqs_code_type ]);

	const toggleGraphicInfoField = () => {
		if(state.reqs_graphic_info != '') change('reqs_ocr', false);
		//change('reqs_graphic_info', ((state.reqs_graphic_info != '') ? '' : '1'));

		$.post(`/promotions/togglegraphicfield/${state.promotion_id}`, { stage_id: state.id }, (response) => {
			change('reqs_graphic_info', ((response.id == null) ? '' : response.id));
		}, 'JSON');
	};

	const prereqs = [
		{ k:'', v:i18n("No requirement") },
		{ k:1, v:i18n("Validate a code"), icon:'knk-promotional-code' },
		{ k:2, v:i18n("Watch a video"), icon:'knk-video' },
		{ k:5, v:i18n("Scan a QR Code"), icon:'knk-qr' },
		{ k:6, v:i18n("Additional fields"), icon:'knk-list' },
	];

	if(props.promotion.loyalty_enabled) {
		prereqs.push({ k:7, v:i18n("Spend virtual coins"), icon:'knk-loyalty' });
	};

	const changeLoyalty = (k, v) => {
		if(state.reqs_loyalty_amount == undefined) {
			const tokens = props.loyaltyTokensStore.getState();
			change('reqs_loyalty_amount', [
				{ id:uuidv4(), type:'reqs_loyalty', token_id:tokens.tokens.tokens[0].LoyaltyToken.id, amount:v },
			]);
			return;
		}
		state.reqs_loyalty_amount[0].amount = v;
		change('reqs_loyalty_amount', state.reqs_loyalty_amount);
	};

	if(props.promotion.is_multistage) {
		prereqs.push({ k:4, v:i18n("Complete other participation stages"), icon:'knk-accept-user' });
	};

	prereqs.push({ k:3, v:i18n("Purchase receipt (manual validation)"), icon:'knk-buy-ticket' });

	if(window?.features.includes('ocr_ticket_ai') && parseInt(props.promotion.promo_type) == 2) {
		prereqs.push({ k: 8, v:i18n("Purchase receipt (AI-powered validation)"), icon:'knk-buy-ticket' });
	};

	const code_freq = [
		{ k:1, v:i18n("On each participation") },
		{ k:0, v:i18n("On the first participation only") },
	];

	const reqs_code_types = [
		{ k:'0', v:"-- " + i18n("Select one") + " --" },
		{ k:'1', v:i18n("Use a unique code") },
		{ k:'2', v:i18n("Use a code set") },
		{ k:'3', v:i18n("Use a regular expression") },
	];

	const codesets = props.codesets ? [{ k:0, v:`-- ${i18n("Choose code set")} --` }].concat(props.codesets.map((c) => {
		const total = (c.CodeSet.external_table == '1') ? c.CodeSet.external_table_count : c.CodeSet.total_codes;
		return { k:c.CodeSet.id, v:`${c.CodeSet.name} (${formatBigNumber(total)})` }
	})) : [];

	const enter_button_label = i18n("Label for '%s' button").replace('%s', i18n("Enter"));
	const continue_button_label = i18n("Label for '%s' button").replace('%s', i18n("Continue"));
	const enable_ocr_validation = (props.promotion.promo_type >= 1) ? true : false;

	const ocr_upgrade = (
		<Message
			_class={{ other: 'in_bottom' }}
		>
			<p><strong>{i18n("OCR technology is a White Label feature.")}</strong></p>

			<p>
				<ActionLink
					_target='_blank'
					_href={`${window.zendesk_base_url}4402344157588`}
				>Learn all you can do with the automatic ticket validation via OCR technology</ActionLink>
			</p>

			<p>
				<ActionLink
					_target='_blank'
					_href={`${window.zendesk_base_url}4403897289876`}
				>How to convert this promotion to White Label</ActionLink>
			</p>
		</Message>
	);

	const avail_locales = available_locales();

	let is_video_id_valid = false;
	if(hasReqs && state.reqs_type == 2) {
		if((state.reqs_video_id != undefined && state.reqs_video_id.match(/^http/i) == null && state.reqs_video_id.match(/youtu?/i) == null)) {
			is_video_id_valid = true;
		}
	}

	let is_qr_static = true;
	let point_of_sales = [];
	if(hasReqs && state.reqs_type == 5) {
		if(state.reqs_qr_type == 'dynamic') is_qr_static = false;

		validation_sites.map((pos, i) => {
			const _pos = pos.ValidationSite
			point_of_sales.push({ id: _pos.id, title: _pos?.name ?? `${i18n("Point of Sale")}: #${_pos.id}` });
		})

	}

	const regenerateQR = () => {
		change('reqs_qr_code', qr_code_generate(state.id));
	};

	// We need the second parameter ("foo") because the <MultiPicker /> calls the action with two parameters,
	// but in this case it's not used anywhere:
	const updatePosList = (pos, foo) => {
		if(pos.length == 0) {
			change('reqs_pos', 'all');
			return;
		}

		if(pos == 'all') {
			change('reqs_pos', []);
			return;
		}

		change('reqs_pos', pos);
	};

	const deletePos = (pos_id) => {
		if(state.reqs_pos.length == 1) {
			change('reqs_pos', 'all');
			return;
		}
		change('reqs_pos', state.reqs_pos.filter((s) => (s != pos_id)));
	};

	const requirement_complete_other_stage = (state.reqs_type == 4) ? true : false;
	const requirement_adv_ocr = (state.reqs_type == 8) ? true : false;
	const punch_card_stage = (state.promotion_type_id == stage_promotion_types.STAMP_CARD.id) ? true : false;

	let default_description;
	let smart_tags = [];
	if(state.reqs_type == '7') {
		smart_tags = [{
			label: i18n("Total coins"),
			tag: 'total_coins',
		}, {
			label: i18n("Coin Name"),
			tag: 'coin_name',
		}, {
			label: i18n("Required Coins"),
			tag: 'required_coins',
		}];

		default_description = `<h2>${i18n("You have {total_coins} {coin_name}.")}</h2><p>${i18n("Each participation costs you {required_coins} {coin_name}.")}</p>`;
	}

	const selected_prereq = prereqs.find((obj) => (obj.k == state.reqs_type));

	const domain = props?.domain ?? undefined;
	const protocol = `${window.location.href.split("/")[0]}//`;
	const domain_public_url = `${(domain) ? `${protocol}${domain.domain}/` : window.public_url}`;

	const default_instructions_placeholder = `<p>${i18n("The photo of the receipt must meet these requirements")}:</p><ul><li>${i18n("Only one receipt appears.")}</li><li>${i18n("The whole receipt appears.")}</li><li>${i18n("All content is readable.")}</li><li>${i18n("The receipt is not wrinkled or folded.")}</li><li>${i18n("The receipt has no stains.")}</li><li>${i18n("The reverse of the receipt is not visible. Avoid backlit photos.")}</li><li>${i18n("Make sure the receipt is in focus.")}</li></ul>`;

	const enable_old_ocr = (is_wl && account_id < 1328486) ? true : false;

	const only_fields = ['field', 'dropdown', 'autocomplete', 'signature', 'attach', 'radio_button', 'checkbox'];
	if (window.features.includes('multi_checkbox')) {
		only_fields.push('multi_checkbox');
	}
	return (
		<div>
			<Model model={state} change={change}>
				<Select
					name="reqs_type"
					_class={{ first: true }}
					values={prereqs}
				>Do you want to add a participation requirement?</Select>

				{(hasReqs && !requirement_complete_other_stage && !punch_card_stage && !requirement_adv_ocr) && (
					<Select
						name="reqs_freq"
						value={(state.reqs_freq == undefined) ? 0 : state.reqs_freq}
						values={code_freq}
					>When do you want to apply the requirement?</Select>
				)}
			</Model>

			{(hasReqs && state.reqs_type == 7) && (
				<Model model={state} change={changeLoyalty}>
					<Input
						name="reqs_loyalty_amount"
						label_props={{
							tooltip: <Tooltip>Number of virtual coins the user must spend to participate. For example, in a prize wheel, it is the number of coins the user will spend on each spin.</Tooltip>,
						}}
						_type='number'
						value={state?.reqs_loyalty_amount?.[0]?.amount ?? ""}
					>Cost of participation in virtual coins</Input>
				</Model>
			)}

			{(hasReqs && state.reqs_type == 8) && (
				<p class="gui_not_in_top_tiny">
					<ActionLink
						_href={`${window.zendesk_base_url}34968848348308`}
						_target='_blank'
					>What is it & how it works?</ActionLink>
				</p>
			)}

			<article class="gui_not_in_top_huge">
				{(hasReqs && state.reqs_type == 1) && (
					<div>
						<BuilderSubSectionTitle
							title={selected_prereq?.v ?? false}
							icon={selected_prereq?.icon ?? false}
							autotranslate={false}
						/>

						<Model model={state} change={change}>
							<Input
								name="reqs_code_label"
								placeholder="Enter the code"
							>Field label</Input>

							<Select
								name="reqs_code_type"
								values={reqs_code_types}
							>Choose the code validation</Select>

							{(state.reqs_code_type == 1) && (
								<Input
									name="reqs_code_unique"
								>Unique code</Input>
							)}

							{(state.reqs_code_type == 3) && (
								<Input
									name="reqs_code_regexp"
								>Regular expression</Input>
							)}

							{(isNotEmpty(state, 'reqs_code_type', '0')) && (
								<Checkbox
									name="reqs_code_scan"
								>Allow barcode or QR code scanning</Checkbox>
							)}
						</Model>

						{(state.reqs_code_type == 2) && (
							<div>
								{(props.codesets.length > 0) ? (
									<Select
										name="reqs_code_set"
										values={codesets}
										model={state}
										change={change}
									>Select the code set</Select>
								) : (
									<p>{i18n("You don't have any code set created")}</p>
								)}

								<ActionLink
									onClick={() => toggleAddCodes(true)}
								>Create a code set</ActionLink>

								{(addCodes) && (
									<Provider store={props.winnerStore}>
										<AddCodeSet
											intl={props.intl}
											codeset={codeset}
											onSave={codeSetAdded}
											backButton={() => toggleAddCodes(false)}
											promotion_id={state.promotion_id}
										/>
									</Provider>
								)}
							</div>
						)}

						{(state.reqs_code_type != 1) && (
							<Model model={state} change={change}>
								<Checkbox
									name="reqs_code_one_use"
								>Codes are single-use</Checkbox>

								{(state.reqs_code_one_use) && (
									<Checkbox
										name="reqs_code_one_use_global"
									>Verify that the code is unique across all stages of the promotion</Checkbox>
								)}
							</Model>
						)}

						{(enable_old_ocr) && (
							<Checkbox
								value={isNotEmpty(state, 'reqs_graphic_info', [''])}
								change={toggleGraphicInfoField}
								name="enable_graphic_proof"
							>Participants must upload an image to confirm the code</Checkbox>
						)}

						{(isNotEmpty(state, 'reqs_graphic_info', [''])) && (
							<Checkbox
								model={state}
								disabled={!enable_ocr_validation}
								change={change}
								name="reqs_ocr"
								after_component={(state.reqs_ocr) ? <ActionLink _href={`#${default_path}/ocr`}>Edit settings</ActionLink> : null}
							>Enable image content verification via OCR</Checkbox>
						)}

						{(isNotEmpty(state, 'reqs_graphic_info', [''])) && (
							<SectionPremoderateTool
								state={state}
								change={change}
								label="Enable code image premoderation"
								tooltip="When the user uploads an image with the code, it will be pending review. A moderator or promotion administrator will have a console to review pending images with codes and approve or reject them. The user will receive an email when their image with code has been approved or rejected. If approved, they can finalize their participation; if rejected, they will need to upload a new image with code. Click on configuration to customize the different messages and emails of the moderation process."
								promotion={props.promotion}
								saveStage={saveStage}
								isCorporate={props.isCorporate}
								intl={props.intl}
							/>
						)}

						<article class="gui_not_in_top_huge">
							<DynamicForm
								form={state.reqs_form ?? []}
								can_create={true}
								can_delete={true}
								can_copy={true}
								locales={avail_locales}
								has_translation={(avail_locales.length > 0) ? true : false}
								onChange={(data) => change('reqs_form', data)}
								main_label="Fields"
								only={['field', 'dropdown', 'autocomplete', 'attach']}
								empty_message={`${i18n("No fields have been added")}.`}
							/>
						</article>

						{/*(isNotEmpty(state, 'reqs_graphic_info') && !enable_ocr_validation) && (
							ocr_upgrade
						)*/}

						{(isNotEmpty(state, 'reqs_graphic_info')) && (
							<Model model={state} change={change}>
								<Input
									name="reqs_code_image_label"
									placeholder="Upload an image of the receipt"
								>Image field title</Input>
							</Model>
						)}
					</div>
				)}

				{(hasReqs && state.reqs_type == 2) && (
					<div>
						<BuilderSubSectionTitle
							title={prereqs.find((obj) => (obj.k == state.reqs_type)).v}
							autotranslate={false}
							icon='ep-youtube'
						/>

						<Model model={state} change={change}>
							<Input
								name="reqs_video_id"
								after_component={<ActionLink
									_href={`${window.zendesk_base_url}360043822672`}
									_target='_blank'
								>How to get the ID of a YouTube video</ActionLink>}
								placeholder="jXLPlDlDuAE"
								max_length={15}
							>YouTube video ID</Input>
						</Model>

						{(isNotEmpty(state, 'reqs_video_id') && !is_video_id_valid) && (
							<AlertMessage
								title="Provide YouTube ID only"
							/>
						)}

						{(isNotEmpty(state, 'reqs_video_id') && is_video_id_valid) && (
							<Model model={state} change={change}>
								<Checkbox
									name='reqs_video_is_portrait'
								>Is portrait?</Checkbox>
							</Model>
						)}
					</div>
				)}

				{(hasReqs && state.reqs_type == 3) && (
					<div>
						<BuilderSubSectionTitle
							title={prereqs.find((obj) => { return (obj.k == state.reqs_type) ? true : false; }).v}
							autotranslate={false}
							icon='knk-redeem-code'
						/>

						<Model model={state} change={change}>
							<Input
								name="reqs_code_image_label"
								placeholder="Upload an image of the receipt"
							>Image field title</Input>
						</Model>

						{/*(state.reqs_ocr && enable_ocr_validation) && (
							<OCRTutorialMessage />
						)*/}

						{/*(!enable_ocr_validation) && (
							ocr_upgrade
						)*/}

						<SectionPremoderateTool
							state={state}
							change={change}
							label="Enable receipt premoderation"
							promotion={props.promotion}
							saveStage={saveStage}
							isCorporate={props.isCorporate}
							intl={props.intl}
						/>


							<Model model={state} change={change}>
								<Checkbox
									disabled={!enable_ocr_validation}
									name="reqs_ocr"
									_class={{ other: (enable_old_ocr) ? 'usetiful_legacy_ocr' : '' }}
									after_component={(state.reqs_ocr && enable_ocr_validation && enable_old_ocr) ? <ActionLink _href={`#${default_path}/ocr`}>Edit settings</ActionLink> : null}
									label_props={{
										addition:(enable_old_ocr) ? {
											text:"Legacy",
											styles:{
												'--roman-font':'700',
												'--main-text-color':'var(--ep-red-color)',
											},
										} : null,
										tooltip:(!enable_old_ocr) ? <Tooltip>This option prevents a user from uploading exactly the same image more than once. IT DOES NOT DETECT IF A USER UPLOADS THE SAME RECEIPT USING A DIFFERENT PHOTO. To enable that level of detection, automatic receipt validation with AI is required, as manual validation does not support this functionality.</Tooltip> : null,
									}}
									autotranslate={false}
								>{(enable_old_ocr) ? i18n("Validate using OCR") : i18n("Prevent duplicate images")}</Checkbox>
							</Model>
					

						{(is_wl === false) && (
							<Message
								icon='ep-addon'
								_class={{ other:'addons_information in_bottom' }}
							>
								<p>{i18n("Upgrade your version to unlock AI-Powered Receipt Validation.")} <ActionLink _href={`${window.zendesk_base_url}34968848348308`} _target='_blank'>Learn more</ActionLink></p>
							</Message>
						)}

						<article class="gui_not_in_top_huge">
							<DynamicForm
								form={state?.reqs_form ?? []}
								can_create={true}
								can_delete={true}
								can_copy={true}
								locales={avail_locales}
								has_translation={(avail_locales.length > 0) ? true : false}
								onChange={(data) => change('reqs_form', data)}
								main_label="Fields"
								only={['field', 'dropdown', 'autocomplete', 'attach', 'radio_button']}
								empty_message={`${i18n("No fields have been added")}.`}
							/>
						</article>
					</div>
				)}

				{(hasReqs && state.reqs_type == 8) && (
					<>
						<OCRPacksStatus
							is_builder={true}
						/>

						<article class="gui_not_in_top_huge">
							<OcrTicketSidebar
								stage={state}
								promotion={props.promotion}
								change={change}
								saveStage={saveStage}
							/>
						</article>

						<article class="gui_not_in_top_huge">
							<BuilderSubSectionTitle
								title={i18n("Manual review")}
								subtitle={<>
									{i18n("When a receipt does not meet any of the conditions, it is sent for manual review.")}

									<Tooltip
										autotranslate={false}
									>{`${i18n("When a user uploads a receipt that does not meet all the conditions, the receipt will be marked as ''pending review''.")}<br />${i18n("Before the receipt enters manual review, the user has the opportunity to provide an explanation as to why they believe their receipt is valid. This feedback can help provide additional context for the reviewer.")}<br />${i18n("A reviewer or promotion administrator will have access to a console where they can review pending receipts and make a decision to either approve or reject them.")}<br />${i18n("Once a decision is made, the user will receive an email notification informing them whether their receipt has been approved or rejected.")}<br />${i18n("To customize the messages and emails related to the moderation process, simply click on the ''Edit Settings'' link.")}`}</Tooltip>
								</>}
								autotranslate={false}
								icon='knk-me'
							/>

							<Checkbox
								state={state}
								change={change}
								value={state?.reqs_mandatory_premoderation ?? false}
								label_props={{
									tooltip:<Tooltip>All receipts will have to be reviewed despite fulfilling all automatic conditions.</Tooltip>,
								}}
								name="reqs_mandatory_premoderation"
							>Always force manual review</Checkbox>

							<SectionPremoderateTool
								state={state}
								change={change}
								label="Enable receipt premoderation"
								promotion={props.promotion}
								saveStage={saveStage}
								isCorporate={props.isCorporate}
								intl={props.intl}
							/>
						</article>
					</>
				)}

				{(hasReqs && state.reqs_type == 4) && (
					<div>
						<DynamicFields
							section_title={i18n("Participation stages")}
							section_icon="knk-redeem-code"
							has_translation={TriviaQuestion}
							locales={avail_locales}
							field_types={[{
								type: 'req_stage',
								label: "Add a stage",
								translate: [
									{ key: 'condition_message', type: 'input' }
								],
								title: (f) => {
									if(f.stage_id == undefined) return i18n("Participate in...");

									const st = props.stages.find((s) => s.Stage.id == f.stage_id);
									if(!st) return null;
									const stage_title = getStageName(st.Stage, i18n);

									if(f.score_options == 'min_score' && f.min_score != undefined) {
										return props.intl.formatMessage({ id: "Scored at least {score} points in {stage}" }, { score: f.min_score, stage: stage_title });
									}

									return props.intl.formatMessage({ id: "Participated in {stage}" }, { stage: stage_title });
								}
							}]}
							fields={state?.reqs_stage ?? []}
							onChange={(v) => change('reqs_stage', v)}
						>
							<ReqStage
								stages={props.stages}
								stage_id={state.id}
								intl={props.intl}
							/>
						</DynamicFields>
					</div>
				)}

				{(hasReqs && state.reqs_type == 5) && (
					<div>
						<BuilderSubSectionTitle
							title="QR Code"
							icon='knk-qr'
							subtitle={`${i18n("Participants must scan a QR code to access the stage.")} ${(is_qr_static) ? i18n("Download it for printing.") : ''}`}
						/>

						<Model model={state} change={change}>
							<Select
								name="reqs_qr_type"
								values={[
									{ k:'', v:`${i18n("Static QR")}: ${i18n("recommended for printing or scavenger hunt campaigns")}` },
									{ k:'dynamic', v:`${i18n("Dynamic QR code")}: ${i18n("recommended for registering purchases at the point of sale (POS)")}` }
								]}
							>Select QR code type</Select>
						</Model>

						{(!is_qr_static) && (
							<div class='gui_not_in_top_small'>
								<p>{i18n("The QR code automatically regenerates after each scan. The user will need to scan a QR code shown by the vendor at a store after making a purchase.")} <ActionLink onClick={() => setDisplayDynamicQRInfoLayer(true)}>How does it work?</ActionLink></p>

								{(point_of_sales.length == 0) && (
									<AlertMessage title="Attention">
										<p>{i18n("You have not created any Point of Sale in the promotion.")} <ActionLink _href='#pos'>Create Point of Sale</ActionLink></p>
									</AlertMessage>
								)}

								{(point_of_sales.length > 0) && (
									(((state?.reqs_pos ?? 'all') == 'all') || isEmpty(state,'reqs_pos')) ? (
										<InformationMessage 
											icon={false} 
											_class={{ other:'gui_not_in_bottom_tiny' }}>
											<p>{i18n("Points of sale with access to this QR")}: <strong class="gui_not_in_end_small">{i18n("***All", 'uppercase')}</strong><wbr /><span class="gui_inline_block">[<ActionLink onClick={() => updatePosList('all', false)}>Change</ActionLink>]</span></p>
										</InformationMessage>
									) : (
										<MultiPicker
											items={point_of_sales}
											selected_items={point_of_sales.filter((i) => ((state?.reqs_pos ?? []).includes(i.id)))}
											title="Points of sale"
											open_button_label="Add points of sale"
											layer_title="Add points of sale to the stage"
											change={updatePosList}
											enable_search={false}
											parent_id={false}
											intl={props.intl}
											item_icon='knk-get-users-to-pos'
											deleteItem={deletePos}
											formatItemName={(item) => {
												return item.title;
											}}
										/>
									)
								)}

								{(point_of_sales.length > 0) && (
									<p><ActionLink _href='#pos'>Manage points of sale</ActionLink></p>
								)}
							</div>
						)}

						{(is_qr_static) && (
							<QRViewer
								url={`${domain_public_url}promotions/participate/${state.promotion_id}/${state.id}?code=${state.reqs_qr_code}`}
								onRegenerate={() => {
									setDisplayQRRegenerateLayer(true)
								}}
								_class={{ other: 'not_in_top' }}
							/>
						)}

						{(display_dynamic_qr_info_layer) && (
							<FloatingLayer _class={{ size: 'small' }} backButton={() => setDisplayDynamicQRInfoLayer(false)}>
								<div class='without_actions'>
									<SectionTitle>Dynamic QR code</SectionTitle>

									<p class='gui_not_in_bottom'><strong>{i18n("Use case")}:</strong> {i18n("After making a purchase, the vendor shows the customer a QR code which the user will scan to record the transaction and participate in this participation stage.")}</p>

									<p class='gui_not_in_bottom'><strong>{i18n("Security")}:</strong> {i18n("To prevent fraud, the QR code automatically regenerates after each scan, ensuring uniqueness and preventing users from sharing them.")}</p>

									<p class='gui_not_in_bottom'><strong>{i18n("Vendor Portal")}:</strong> {i18n("The dynamic QR code is an integrated utility within the Vendor Portal. The Vendor Portal is a mobile-optimized web page with various tools aimed at sellers in a point of sale, store, or shop. It is accessed via a URL, and each point of sale must be identified with its own password.")}</p>

									<p><ActionLink
										_href={`${window.zendesk_base_url}25250120393748`}
										_target='_blank'
									>Tutorial of the Dynamic QR Code System</ActionLink></p>
								</div>
							</FloatingLayer>
						)}

						{(display_qr_regenerate_layer) && (
							<FloatingLayer _class={{ size: 'small' }} backButton={() => setDisplayQRRegenerateLayer(false)}>
								<div>
									<SectionTitle _class={{ icon: 'knk-alert redIcon' }}>Attention</SectionTitle>

									<SectionSubtitle>If you regenerate the QR code, you won't be able to return to the current version.</SectionSubtitle>

									<SectionSubtitle autotranslate={false}><strong>{i18n("Any previous version of the QR code that you have printed will cease to function and will not allow users who scan it to participate.")}</strong></SectionSubtitle>

									<SectionSubtitle>You will need to create new prints of the newly generated QR code.</SectionSubtitle>

									<SectionSubtitle>Are you sure you want to continue?</SectionSubtitle>

									<ButtonCollection in_floating={true}>
										<SaveButton
											onClick={(e, cb) => {
												regenerateQR();
												cb();

												setTimeout(() => {
													setDisplayQRRegenerateLayer(false);
												}, 500);
											}}
										>Yes, regenerate QR</SaveButton>

										<SecondaryButton
											onClick={() => setDisplayQRRegenerateLayer(false)}
										>No, cancel</SecondaryButton>
									</ButtonCollection>
								</div>
							</FloatingLayer>
						)}
					</div>
				)}

				{(hasReqs && state.reqs_type == 6) && (
					<DynamicFormBuilder
						form={state.reqs_form ?? []}
						can_create={true}
						can_delete={true}
						can_copy={true}
						locales={avail_locales}
						has_translation={(avail_locales.length > 0) ? true : false}
						onChange={(data) => change('reqs_form', data)}
						main_label="Form"
						only={only_fields}
						empty_message={`${i18n("No fields have been added")}.`}
						section_icon='knk-list'
					/>
				)}
			</article>

			{(hasReqs) && (
				<article class="gui_not_in_top_huge">
					<BuilderSubSectionTitle
						title={(state.reqs_type != 5) ? "Contents" : "Instructions or clue to find the QR code"}
						icon='knk-profile'
					/>

					<Model model={state} change={change}>
						<ImageUploader
							name="reqs_image"
							image={state.full_reqs_image}
							small_input={true}
						>Header image</ImageUploader>

						<RichPlaceHolder
							name="reqs_description"
							smarttags={smart_tags}
							onFocus={() => change('display_valid_qr', false)}
							placeholder={default_description}
							wrapStart='{'
							wrapEnd='}'
						>Title</RichPlaceHolder>

						{(state.reqs_type == 8) && (
							<Input
								name="reqs_code_image_label"
								placeholder="Upload an image of the receipt"
							>Image field title</Input>
						)}

						{(state.reqs_type == 8) && (
							<RichPlaceHolder
								name="reqs_ocr_instructions"
								placeholder={default_instructions_placeholder}
							>Instructions</RichPlaceHolder>
						)}

						{(state.reqs_type != 5) && (
							<Input
								name="reqs_enter_button_label"
								placeholder="Enter"
								autotranslate={false}
							>{enter_button_label}</Input>
						)}
					</Model>
				</article>
			)}

			{(hasReqs && state.reqs_type == 8) && (
				<WaitingSentencesEditor
					stage={state}
					change={change}
					intl={props.intl}
				/>
			)}

			{(hasReqs && state.reqs_type == 5) && (
				<article class="gui_not_in_top_huge">
					<div>
						<BuilderSubSectionTitle
							title="Screen after scanning the QR code"
							icon='knk-on'
						/>

						<Model model={state} change={change}>
							<Select
								name="reqs_qr_mode"
								values={[
									{ k:'', v:i18n("Button to continue") },
									{ k:'automatic', v:i18n("Automatic redirection") }
								]}
								_class={{ size: 50 }}
							>Transition mode</Select>
						</Model>

						{((state?.reqs_qr_mode ?? '') == '') && (
							<Model model={state} change={change}>
								<RichEditor
									name="reqs_description_valid_qr"
									onFocus={() => change('display_valid_qr', true)}
								>Valid QR Message</RichEditor>

								<AutoPostImageUploader
									auto_url={`/promotions/auto_img/${state.promotion_id}`}
									auto_type='stages'
									image={state.full_reqs_image_valid_qr}
									name="reqs_image_valid_qr"
									small_input={true}
									_size={{ fit: 'auto' }}
								>Valid QR Image</AutoPostImageUploader>

								<Input
									name="reqs_enter_button_label"
									placeholder="Continue"
									autotranslate={false}
									_class={{ size: 50 }}
								>{continue_button_label}</Input>
							</Model>
						)}
					</div>
				</article>
			)}
		</div>
	);
}

export default injectIntl(StageReqsSidebar);